import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { Skeleton } from '../../../components/ui/skeleton';

import {
  CheckCircle,
  AlertCircle,
  XCircle,
  Clock,
  Zap,
  Globe,
  Server,
  Activity,
  TrendingUp,
  TrendingDown,
  Database
} from 'lucide-react';

/**
 * Componente de overview general de App Health
 * Muestra métricas clave, estado general de la aplicación y health de Supabase
 */
const AppHealthOverview = ({ healthData, deployments, performanceMetrics, supabaseHealth, isLoading }) => {

  // Obtener último deployment
  const getLatestDeployment = () => {
    if (!deployments || deployments.length === 0) return null;
    return deployments[0];
  };

  // Obtener métricas de uptime
  const getUptimeMetrics = () => {
    if (!healthData) return { uptime: 0, status: 'unknown' };

    return {
      uptime: healthData.uptime || 99.9,
      status: healthData.status || 'unknown',
      responseTime: healthData.responseTime || 0,
      errorRate: healthData.errorRate || 0
    };
  };

  // Obtener estado de servicios
  const getServicesStatus = () => {
    if (!healthData?.services) return [];

    return healthData.services.map(service => ({
      name: service.name,
      status: service.status,
      responseTime: service.responseTime,
      lastCheck: service.lastCheck
    }));
  };

  const latestDeployment = getLatestDeployment();
  const uptimeMetrics = getUptimeMetrics();
  const services = getServicesStatus();

  // Health badge por status de Supabase
  const supabaseStatusColor = (status) => {
    if (status === 'ACTIVE_HEALTHY') return 'default';
    if (status === 'ACTIVE_UNHEALTHY' || status === 'COMING_UP') return 'secondary';
    return 'destructive';
  };

  const supabaseStatusLabel = (status) => {
    if (!status) return '—';
    return status.replace(/_/g, ' ');
  };

  // Renderizar skeleton durante carga
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Uptime */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uptime</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[var(--yool-success)]">
              {uptimeMetrics.uptime.toFixed(2)}%
            </div>
            <Progress value={uptimeMetrics.uptime} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-2">
              Últimos 30 días
            </p>
          </CardContent>
        </Card>

        {/* Response Time */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Response Time</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {uptimeMetrics.responseTime}ms
            </div>
            <div className="flex items-center text-xs text-[var(--yool-success)] mt-2">
              <TrendingDown className="h-3 w-3 mr-1" />
              -12% vs último mes
            </div>
          </CardContent>
        </Card>

        {/* Error Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {uptimeMetrics.errorRate.toFixed(3)}%
            </div>
            <div className="flex items-center text-xs text-[var(--yool-success)] mt-2">
              <TrendingDown className="h-3 w-3 mr-1" />
              Dentro del rango normal
            </div>
          </CardContent>
        </Card>

        {/* Deployments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Deployments</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {deployments?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Este mes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Supabase Health */}
      {supabaseHealth?.projects && supabaseHealth.projects.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-[var(--yool-success)]" />
              Supabase · Estado de Proyectos
            </CardTitle>
            <CardDescription>
              Health en tiempo real de los proyectos Supabase · org: {supabaseHealth.org?.name || 'yooldigital'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {supabaseHealth.projects.map((proj, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  {/* Header del proyecto */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {proj.info?.status === 'ACTIVE_HEALTHY' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : proj.info?.status ? (
                        <AlertCircle className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span className="font-semibold capitalize">{proj.name}</span>
                      <span className="text-xs text-muted-foreground font-mono">{proj.id?.substring(0, 8)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {proj.info?.status && (
                        <Badge variant={supabaseStatusColor(proj.info.status)} className="text-xs capitalize">
                          {supabaseStatusLabel(proj.info.status)}
                        </Badge>
                      )}
                      {proj.info?.region && (
                        <Badge variant="outline" className="text-xs">
                          {proj.info.region}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Servicios del proyecto */}
                  {proj.healthError ? (
                    <p className="text-xs text-destructive">{proj.healthError}</p>
                  ) : proj.health && Array.isArray(proj.health) && proj.health.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {proj.health.map((svc, i) => {
                        const isHealthy = svc.status === 'ACTIVE_HEALTHY';
                        const isWarn = svc.status === 'ACTIVE_UNHEALTHY' || svc.status === 'COMING_UP';
                        return (
                          <div
                            key={i}
                            className={`flex items-center gap-1.5 p-2 rounded-md border text-xs ${
                              isHealthy ? 'border-green-200 bg-green-50' :
                              isWarn ? 'border-yellow-200 bg-yellow-50' :
                              'border-red-200 bg-red-50'
                            }`}
                          >
                            {isHealthy ? (
                              <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />
                            ) : isWarn ? (
                              <AlertCircle className="h-3 w-3 text-yellow-500 shrink-0" />
                            ) : (
                              <XCircle className="h-3 w-3 text-red-500 shrink-0" />
                            )}
                            <span className="font-medium capitalize">
                              {svc.name?.replace(/_/g, ' ')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Sin datos de health disponibles</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estado de servicios Vercel */}
      {services.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Estado de Servicios</CardTitle>
            <CardDescription>
              Monitoreo en tiempo real de servicios críticos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    {service.status === 'healthy' && (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    )}
                    {service.status === 'warning' && (
                      <AlertCircle className="h-5 w-5 text-yellow-500" />
                    )}
                    {service.status === 'error' && (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium">{service.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {service.responseTime}ms
                      </p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      service.status === 'healthy' ? 'default' :
                      service.status === 'warning' ? 'secondary' : 'destructive'
                    }
                  >
                    {service.status === 'healthy' ? 'OK' :
                     service.status === 'warning' ? 'Warning' : 'Error'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Último deployment */}
      {latestDeployment && (
        <Card>
          <CardHeader>
            <CardTitle>Último Deployment</CardTitle>
            <CardDescription>
              Información del deployment más reciente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Estado</p>
                <div className="flex items-center space-x-2 mt-1">
                  {latestDeployment.status === 'success' && (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}
                  {latestDeployment.status === 'error' && (
                    <XCircle className="h-4 w-4 text-red-500" />
                  )}
                  {latestDeployment.status === 'building' && (
                    <Clock className="h-4 w-4 text-yellow-500" />
                  )}
                  <Badge
                    variant={
                      latestDeployment.status === 'success' ? 'default' :
                      latestDeployment.status === 'error' ? 'destructive' : 'secondary'
                    }
                  >
                    {latestDeployment.status}
                  </Badge>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Commit</p>
                <p className="text-sm font-mono mt-1">
                  {latestDeployment.commit?.substring(0, 8) || 'N/A'}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground">Duración</p>
                <p className="text-sm mt-1">
                  {latestDeployment.duration || 'N/A'}
                </p>
              </div>
            </div>

            {latestDeployment.url && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground">URL</p>
                <a
                  href={latestDeployment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center space-x-1 mt-1"
                >
                  <Globe className="h-3 w-3" />
                  <span>{latestDeployment.url}</span>
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Métricas de performance resumidas */}
      {performanceMetrics && (
        <Card>
          <CardHeader>
            <CardTitle>Performance Summary</CardTitle>
            <CardDescription>
              Métricas clave de rendimiento de la aplicación
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {performanceMetrics.requests?.total || 0}
                </p>
                <p className="text-sm text-muted-foreground">Requests/día</p>
              </div>

              <div className="text-center">
                <p className="text-2xl font-bold text-[var(--yool-success)]">
                  {performanceMetrics.bandwidth?.total || '0 GB'}
                </p>
                <p className="text-sm text-muted-foreground">Bandwidth</p>
              </div>

              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {performanceMetrics.functions?.invocations || 0}
                </p>
                <p className="text-sm text-muted-foreground">Function Calls</p>
              </div>

              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {performanceMetrics.edge?.requests || 0}
                </p>
                <p className="text-sm text-muted-foreground">Edge Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AppHealthOverview;
